import React, { useCallback, useEffect, useState } from "react";
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Container,
    Dropdown,
    Form,
    Modal,
    Pagination,
    Row,
    Table,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getAllLeaveTypeAllotments,
    getAllLeaveTypes,
    saveOrUpdateLeaveTypeAllotment,
} from "../../../../api/onboarddeboard";
import { getAllRoles } from "../../../../api/roleAllocation";

const LeaveTypeAllotment = () => {
    const [editingId, setEditingId] = useState(null);
    const [status, setStatus] = useState(true);
    const token = localStorage.getItem("token");

    const [leaveTypes, setLeaveTypes] = useState([]);
    const [userTypes, setUserTypes] = useState([]); // roles, kept name for minimal diff

    const [leaveTypeId, setLeaveTypeId] = useState("");
    const [selectedUserTypes, setSelectedUserTypes] = useState([]);
    const [totalLeaves, setTotalLeaves] = useState("");
    const [reason, setReason] = useState("");
    const [tableData, setTableData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [errors, setErrors] = useState({});
    const ITEMS_PER_PAGE = 5;
    const [frozenStaffIds, setFrozenStaffIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOption, setSortOption] = useState("latest");
    const [filterStatus, setFilterStatus] = useState("");
    const [originalStaffStatus, setOriginalStaffStatus] = useState({});
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [originalData, setOriginalData] = useState(null);

    const fetchLeaveTypeAllotments = useCallback(async () => {
        try {
            const response = await getAllLeaveTypeAllotments(token);
            setTableData(response.data || []);
        } catch (error) {
            toast.error("Failed to fetch data.");
        }
    }, [token]);

    const groupedData = Object.values(
        tableData.reduce((acc, item) => {
            const key = item.leaveType.id;

            if (!acc[key]) {
                acc[key] = {
                    leaveTypeName: item.leaveType.leaveName,
                    leaveTypeId: item.leaveType.id,
                    rows: [],
                };
            }

            acc[key].rows.push({
                id: item.id,
                leaveTypeId: item.leaveType.id,
                leaveTypeName: item.leaveType.leaveName,
                staff: item.role?.role,
                userTypeId: item.role?.id,
                totalLeaves: item.totalLeaves,
                status: item.status,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
            });
            return acc;
        }, {}),
    );

    const processedData = groupedData
        .map((group) => {
            const filteredRows = group.rows.filter((row) => {
                const matchesSearch =
                    group.leaveTypeName
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    row.staff?.toLowerCase().includes(searchTerm.toLowerCase());

                const matchesStatus =
                    filterStatus === "" ? true : row.status === (filterStatus === "true");

                return matchesSearch && matchesStatus;
            });

            return {
                ...group,
                rows: filteredRows,
            };
        })
        .filter((group) => group.rows.length > 0)
        .sort((a, b) => {
            switch (sortOption) {
                case "nameAZ":
                    return a.leaveTypeName.localeCompare(b.leaveTypeName);
                case "nameZA":
                    return b.leaveTypeName.localeCompare(a.leaveTypeName);
                case "latest":
                default: {
                    const getLatestDate = (group) =>
                        Math.max(
                            ...group.rows.map((r) =>
                                new Date(r.updatedAt || r.createdAt || 0).getTime()
                            )
                        );
                    return getLatestDate(b) - getLatestDate(a);
                }
            }
        });

    const totalPages = Math.ceil(processedData.length / ITEMS_PER_PAGE);

    const paginatedGroups = processedData.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
    );

    const fetchLeaveTyps = useCallback(async () => {
        try {
            const response = await getAllLeaveTypes(token);
            setLeaveTypes(response.data);

            const userRes = await getAllRoles(token);
            setUserTypes(userRes.data);
        } catch (error) {
            toast.error("Failed to fetch leave types.");
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchLeaveTypeAllotments();
        fetchLeaveTyps();
    }, [token, fetchLeaveTypeAllotments, fetchLeaveTyps]);


    const getTotalLeavesError = (value) => {
        const tl = String(value);

        if (tl.length === 0) {
            return "Max Leaves is required";
        }
        if (/^\s+$/.test(tl)) {
            return "Max Leaves cannot be only spaces.";
        }
        if (!/^\d+$/.test(tl)) {
            return "Letters and special characters are not allowed. Only numbers allowed.";
        }
        if (Number(tl) <= 0) {
            return "Max Leaves must be greater than 0";
        }
        return "";
    };



    const handleSubmit = (e) => {
        e.preventDefault();


        const newlyAddedUserTypes = selectedUserTypes.filter(
            (id) => !frozenStaffIds.includes(id)
        );


        const availableStaffCount = userTypes.filter(
            (ut) => ![2, 5].includes(ut.id)
        ).length;

        //validateForm(newlyAddedUserTypes); 

        

        if (
            !editingId &&
            leaveTypeId &&
            frozenStaffIds.length > 0 &&
            frozenStaffIds.length === availableStaffCount &&
            newlyAddedUserTypes.length === 0
        ) {
            toast.error(
                "Leave allotment already exists for all selected staff. Please use Edit to modify it"
            );
            return;
        }
        const totalLeavesError = getTotalLeavesError(totalLeaves);
        if (totalLeavesError) {
            setErrors((prev) => ({ ...prev, totalLeaves: totalLeavesError }));
        }

        if (
            !editingId &&
            leaveTypeId &&
            frozenStaffIds.length > 0 &&
            frozenStaffIds.length < availableStaffCount &&
            newlyAddedUserTypes.length === 0
        ) {
            setErrors((prev) => ({
                ...prev, userTypes: " Please select at least one new staff .",
            }));
            return;
        }

        if (!validateForm(newlyAddedUserTypes)) return;

        const duplicate = isDuplicateEntry();
        if (duplicate) {
            toast.error("Leave Type already assigned to selected staff.");
            return;
        }

        if (editingId && !hasChanges()) {
            toast.warning("No changes detected.");
            return;
        }

        // NEW: while editing, keep full selectedUserTypes (existing update flow).
        // While adding fresh, only send the newly added (non-frozen) staff.
        const rolesToSend = editingId ? selectedUserTypes : newlyAddedUserTypes;

        const payload = {
            id: editingId || undefined,     // NEW — target exact entry on update
            leaveTypeId: Number(leaveTypeId),
            totalLeaves: Number(totalLeaves),
            roles: rolesToSend.map((id) => ({
                userTypeId: id,
                status: status,
            })),
            reason: null,
        };

        if (editingId && originalData?.status === true && status === false) {
            setPendingPayload(payload);
            setShowReasonModal(true);
            return;
        }

        submitData(payload);
    };

    const submitData = async (payload) => {
        try {
            await saveOrUpdateLeaveTypeAllotment(payload, token);
            toast.success("Saved successfully");
            fetchLeaveTypeAllotments();

            setLeaveTypeId("");
            setSelectedUserTypes([]);
            setTotalLeaves("");
            setStatus(true);
            setReason("");
            setEditingId(null);
            setFrozenStaffIds([]); // NEW: reset freeze list after successful save
        } catch (error) {
            toast.error("Failed to submit");
        }
    };

    // NEW: accepts newlyAddedUserTypes so "add fresh" mode validates against
    // only the newly picked staff, not the frozen/pre-existing ones.
    const validateForm = (newlyAddedUserTypes) => {
        const newErrors = {};

        if (!leaveTypeId) {
            newErrors.leaveTypeId = "Leave Type is required";
        }

        if (!editingId && (newlyAddedUserTypes ?? selectedUserTypes).length === 0) {
            newErrors.userTypes = "Please select at least one Staff";
        }

        const totalLeavesError = getTotalLeavesError(totalLeaves);
        if (totalLeavesError) {
            newErrors.totalLeaves = totalLeavesError;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = () => {
        if (!originalData) return true;

        const currentUserTypes = [...selectedUserTypes].sort();

        return (
            Number(leaveTypeId) !== originalData.leaveTypeId ||
            Number(totalLeaves) !== originalData.totalLeaves ||
            JSON.stringify(currentUserTypes) !==
            JSON.stringify(originalData.selectedUserTypes) ||
            status !== originalData.status
        );
    };

    const handleEdit = (row) => {
        setErrors({});
        setOriginalData(null);

        // CHANGED: only the clicked row's single staff, not all staff under this leaveType
        const singleRoleId = row.userTypeId ? [row.userTypeId] : [];

        setEditingId(row.id);
        setLeaveTypeId(row.leaveTypeId);
        setSelectedUserTypes(singleRoleId);
        setFrozenStaffIds(singleRoleId);
        setOriginalStaffStatus({ [row.userTypeId]: row.status });
        setTotalLeaves(row.totalLeaves);
        setStatus(row.status);

        setOriginalData({
            leaveTypeId: row.leaveTypeId,
            selectedUserTypes: singleRoleId,
            totalLeaves: Number(row.totalLeaves),
            status: row.status,
        });
    };

    const handleUserTypeCheck = (id) => {
        setSelectedUserTypes((prev) => {
            let updated;
            if (prev.includes(id)) {
                updated = prev.filter((item) => item !== id);
            } else {
                updated = [...prev, id];
            }
            return updated;
        });
    };

    const handleConfirmReason = () => {
        if (errors.deactivateReason) {
            return;
        }

        const updatedPayload = {
            ...pendingPayload,
            reason: reason.trim() || null,
        };

        submitData(updatedPayload);
        setShowReasonModal(false);
        setPendingPayload(null);
        setReason("");
    };

    const handleDeactivateReasonChange = (e) => {
        const value = e.target.value;

        if (value.length === 1 && !/[a-zA-Z0-9]/.test(value)) {
            setErrors((prev) => ({
                ...prev,
                deactivateReason: "First character cannot be a special character.",
            }));
            return;
        }

        if (value.length > 300) {
            setErrors((prev) => ({
                ...prev,
                deactivateReason: "Reason cannot exceed 300 characters.",
            }));
            return;
        }
        setReason(value);
        if (value.trim() && !/[a-zA-Z]/.test(value)) {
            setErrors((prev) => ({
                ...prev,
                deactivateReason: "Reason must contain at least one letter.",
            }));
            return;
        }

        setErrors((prev) => ({
            ...prev,
            deactivateReason: "",
        }));
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortOption, filterStatus]);

    const isDuplicateEntry = () => {
        const currentLeaveId = Number(leaveTypeId);


        const idsToCheck = editingId
            ? selectedUserTypes
            : selectedUserTypes.filter((id) => !frozenStaffIds.includes(id));


        return tableData.some((item) => {
            const itemLeaveId = Number(item.leaveType.id);

            const belongsToEditingEntry =
                editingId &&
                itemLeaveId === currentLeaveId &&
                originalData?.selectedUserTypes.includes(item.role?.id);

            if (belongsToEditingEntry) return false;

            return idsToCheck.some(
                (userTypeId) => itemLeaveId === currentLeaveId && item.role.id === userTypeId
            );
        });
    };

    const handleCancel = () => {
        setEditingId(null);
        setLeaveTypeId("");
        setSelectedUserTypes([]);
        setTotalLeaves("");
        setReason("");
        setStatus(true);
        setFrozenStaffIds([]);
        setErrors({});
        setOriginalData(null);
    };

    const exportToCSV = () => {
        if (processedData.length === 0) {
            toast.error("No data available");
            return;
        }

        const headers = ["Leave Type", "Staff", "Max Leaves", "Status"];
        const rows = [];

        processedData.forEach((group) => {
            group.rows.forEach((row) => {
                rows.push([
                    group.leaveTypeName,
                    row.staff,
                    row.totalLeaves,
                    row.status ? "Active" : "Inactive",
                ]);
            });
        });

        const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "leave_type_allotment.csv";
        a.click();
    };

    return (
        <Container fluid className="p-4">
            <style>{`
            .leave-toggle .form-check-input:checked {
    background-color: #2E5CB8 !important;
    border-color: #2E5CB8 !important;
}
.leave-toggle .form-check-input:focus {
    border-color: #2E5CB8 !important;
    box-shadow: 0 0 0 0.1rem rgba(46, 92, 184, 0.35) !important;
}
            .lc-breadcrumb a {
                text-decoration: none !important;
                color: #2E5CB8 !important;
            }
            .lc-breadcrumb .breadcrumb-item.active {
                color: #6c757d !important;
            }
            .pagination .page-item.active .page-link {
                background-color: #2E5CB8 !important;
                border-color: #2E5CB8 !important;
            }
        `}</style>
<h1 className="page-main-heading" style={{ marginBottom: 12 }}>
                Leave Type Allotment
            </h1>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnHover
            />
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
                <Breadcrumb.Item href="#">Master</Breadcrumb.Item>
                <Breadcrumb.Item active>Leave Type Allotment</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="mb-4">
                <Col xl={12} lg={12} md={12} xs={12}>
                    <Card>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Leave Type<span className="text-danger"> *</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Select
                                                    value={leaveTypeId}
                                                    disabled={editingId !== null}
                                                    onChange={(e) => {
    const selectedId = e.target.value;
    setLeaveTypeId(selectedId);
    setErrors((prev) => ({ ...prev, leaveTypeId: "", userTypes: "", totalLeaves: "" })); // now also clears totalLeaves

    if (selectedId && !editingId) {
        const existingRoleIds = tableData
            .filter((item) => Number(item.leaveType.id) === Number(selectedId))
            .map((item) => item.role?.id)
            .filter(Boolean);
        setSelectedUserTypes(existingRoleIds);
        setFrozenStaffIds(existingRoleIds);
    }
}}
                                                >
                                                    <option value="">Select Leave Type</option>
                                                    {leaveTypes
                                                        .filter((lt) => lt.status === true)
                                                        .map((lt) => (
                                                            <option key={lt.id} value={lt.id}>
                                                                {lt.leaveName}
                                                            </option>
                                                        ))}
                                                </Form.Select>
                                                {errors.leaveTypeId && (
                                                    <div className="text-danger" style={{ fontSize: "13px" }}>
                                                        {errors.leaveTypeId}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Staff<span className="text-danger"> *</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Dropdown>
                                                    <Dropdown.Toggle
                                                        variant="light"
                                                        bsPrefix="no-caret"
                                                        disabled={editingId !== null}
                                                        className="w-100 d-flex justify-content-between align-items-center"
                                                        style={{
                                                            border: "1px solid #ced4da",
                                                            borderRadius: "4px",
                                                            backgroundColor: editingId !== null ? "#e9ecef" : "inherit",
                                                        }}
                                                    >
                                                        {editingId !== null
                                                            ? (userTypes.find((ut) => ut.id === selectedUserTypes[0])?.role || "Staff")
                                                            : selectedUserTypes.length > 0
                                                                ? `${selectedUserTypes.length} Staff Selected`
                                                                : "Select Staff"}
                                                        <span><i className="bi bi-chevron-down"></i></span>
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu
                                                        className="px-3"
                                                        style={{ maxHeight: "250px", overflowY: "auto", width: "100%" }}
                                                    >
                                                        {userTypes.length === 0 && (
                                                            <div className="text-muted">No staff available</div>
                                                        )}

                                                        {userTypes
                                                            .filter((ut) => ![2, 5].includes(ut.id))
                                                            .map((ut) => {
                                                                const isChecked = selectedUserTypes.includes(ut.id);
                                                                return (
                                                                    <Form.Check
                                                                        key={ut.id}
                                                                        type="checkbox"
                                                                        className="py-1"
                                                                        label={ut.role}
                                                                        checked={isChecked}
                                                                        disabled={frozenStaffIds.includes(ut.id)}
                                                                        onChange={() => {
                                                                            handleUserTypeCheck(ut.id);
                                                                            setErrors((prev) => ({ ...prev, userTypes: "" }));
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                                {errors.userTypes && (
                                                    <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                                                        {errors.userTypes}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Max Leaves<span className="text-danger"> *</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Control
                                                    type="text"
                                                    value={totalLeaves}
                                                    placeholder="Enter max leaves"
                                                    maxLength={3}
                                                    onChange={(e) => {
                                                        const value = e.target.value.slice(0, 3);
                                                        setTotalLeaves(value);
                                                        setErrors((prev) => ({ ...prev, totalLeaves: value ? getTotalLeavesError(value) : "" }));
                                                    }}
                                                />
                                                {errors.totalLeaves && (
                                                    <div className="text-danger" style={{ fontSize: "13px" }}>
                                                        {errors.totalLeaves}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">Active</Form.Label>
                                            <Col sm={8} className="d-flex align-items-center gap-2">
                                                <div className="leave-toggle">
                                                    <Form.Check
                                                        type="switch"
                                                        checked={status}
                                                        onChange={(e) => setStatus(e.target.checked)}
                                                    />
                                                </div>
                                                <span style={{ fontSize: "14px", color: "#6c757d" }}>if Active</span>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Row className="align-items-center mt-5">
                                    <Col className="mt-2 d-flex align-items-center justify-content-center">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}
                                        >
                                            {editingId ? "Update" : "Submit"}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            style={{ marginLeft: "10px" }}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>

                            <Row className="mb-4 mt-5">
                                <Col xl={12} lg={12} md={12} xs={12}>
                                    <Row className="mb-3 g-2 align-items-center justify-content-end flex-wrap">
                                        <Col xs={12} sm={6} md={3}>
                                            <div style={{ position: "relative" }}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search by..."
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    style={{ paddingRight: "35px" }}
                                                />
                                                {searchTerm && (
                                                    <i
                                                        className="bi bi-x-circle"
                                                        onClick={() => {
                                                            setSearchTerm("");
                                                            setCurrentPage(1);
                                                        }}
                                                        style={{
                                                            position: "absolute",
                                                            right: "10px",
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                            cursor: "pointer",
                                                            color: "#999",
                                                            fontSize: "16px",
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </Col>

                                        <Col xs={6} sm={3} md={2}>
                                            <Form.Select
                                                value={sortOption}
                                                onChange={(e) => {
                                                    setSortOption(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value="latest">Latest First</option>
                                                <option value="nameAZ">Sort (A-Z)</option>
                                                <option value="nameZA">Sort (Z-A)</option>
                                            </Form.Select>
                                        </Col>

                                        <Col xs={6} sm={3} md={2}>
                                            <Form.Select
                                                value={filterStatus}
                                                onChange={(e) => {
                                                    setFilterStatus(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value="">All</option>
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </Form.Select>
                                        </Col>

                                        <Col xs="auto" className="d-flex justify-content-end" md="auto">
                                            <i
                                                className="bi bi-download rounded"
                                                title="Export CSV"
                                                style={{
                                                    cursor: "pointer",
                                                    color: "#2E5CB8",
                                                    border: "1px solid #2E5CB8",
                                                    padding: "7px",
                                                    paddingLeft: "10px",
                                                    paddingRight: "10px",
                                                }}
                                                onClick={exportToCSV}
                                            />
                                        </Col>
                                    </Row>

                                    <Table bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "8%", textAlign: "center" }}>Sr. No.</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "20%" }}>Leave Type</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "25%", textAlign: "center" }}>Staff</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "15%", textAlign: "center" }}>Max Leaves</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "15%", textAlign: "center" }}>Status</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "18%", textAlign: "center" }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedGroups.length > 0 ? (
                                                paginatedGroups.flatMap((group) => group.rows).map((row, index) => (
                                                    <tr key={row.id}>
                                                        <td style={{ textAlign: "center" }}>
                                                            {(currentPage - 1) * ITEMS_PER_PAGE * 1 + index + 1}
                                                        </td>
                                                        <td>{row.leaveTypeName}</td>
                                                        <td style={{ textAlign: "center" }}>{row.staff}</td>
                                                        <td style={{ textAlign: "center" }}>{row.totalLeaves}</td>
                                                        <td style={{ textAlign: "center" }}>{row.status ? "Active" : "Inactive"}</td>
                                                        <td className="text-center align-middle">
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={() => handleEdit(row)}
                                                            >
                                                                <i className="bi bi-pencil-square"></i> Edit
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted">
                                                        {searchTerm ? "No matching records found" : "No data available"}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>

                                    <div className="d-flex justify-content-end">
                                        <Pagination>
                                            <Pagination.Prev
                                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                                disabled={currentPage === 1}
                                            />
                                            {[...Array(totalPages).keys()].map((number) => (
                                                <Pagination.Item
                                                    key={number + 1}
                                                    active={number + 1 === currentPage}
                                                    onClick={() => setCurrentPage(number + 1)}
                                                >
                                                    {number + 1}
                                                </Pagination.Item>
                                            ))}
                                            <Pagination.Next
                                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle-fill text-danger me-2" />
                        Deactivate Staff
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Some staff will be marked as inactive. You can provide a reason (optional).
                    </p>
                    <Form.Group>
                        <Form.Label>Reason <span className="text-muted">(optional)</span></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={reason}
                            onChange={handleDeactivateReasonChange}
                            placeholder="Enter reason"
                        />
                        <div style={{
                            fontSize: "12px",
                            color: reason.length > 300 ? "red" : "#6c757d",
                            textAlign: "right",
                        }}>
                            {reason.length}/300 characters
                        </div>
                        {errors.deactivateReason && (
                            <p style={{ color: "red", fontSize: "13px" }}>{errors.deactivateReason}</p>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReasonModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleConfirmReason}>Confirm</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LeaveTypeAllotment;
